import React, { useState, useEffect } from 'react';
import { Contact } from '../../types';
import { ComboboxSelector } from '../inputs/ComboboxSelector';
import { useOrigins } from '../../features/settings/origins/hooks/useOrigins';
import Input from '../ui/Input';
import { Button } from '../ui/Button';
import { SidebarHeader } from '../../features/kanban/components/SidebarHeader';
import { BRAZIL_PHONE_PREFIX, formatPhoneWithDDI } from '../../utils/formatters';
import { isUsableContactName } from '../../utils/phoneUtils';
import { CheckIcon } from '../icons';

interface ContactFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contactData: Omit<Contact, 'id'>) => Promise<void>;
  contact?: Contact | null;
  contained?: boolean;
  lockPhone?: boolean;
}

const STORAGE_KEY = 'contact_form_data';
const EMPTY_FORM = { name: '', email: '', whatsapp: BRAZIL_PHONE_PREFIX, company: '', origin_id: '' };

const TEXT_FIELDS = [
  { name: 'name', label: 'Nome *', type: 'text', autoComplete: 'name', placeholder: 'Digite o nome do contato...', required: true },
  { name: 'company', label: 'Empresa', type: 'text', autoComplete: 'organization', placeholder: 'Nome da empresa...' },
  { name: 'email', label: 'Email', type: 'email', autoComplete: 'email', placeholder: 'Digite o email do contato...' },
] as const;

const formFromContact = (contact: Contact) => {
  const phone = contact.whatsapp || contact.phone || '';
  return {
    name: isUsableContactName(contact.name, phone) ? contact.name : '',
    email: contact.email || '',
    whatsapp: formatPhoneWithDDI(phone || BRAZIL_PHONE_PREFIX),
    company: contact.company || '',
    origin_id: contact.origin_id || '',
  };
};

const loadForm = (contact?: Contact | null) => {
  if (contact) return formFromContact(contact);
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return { ...EMPTY_FORM };
};

export default function ContactFormSheet({
  isOpen,
  onClose,
  onSave,
  contact,
  contained = false,
  lockPhone = false,
}: ContactFormSheetProps) {
  const { origins, loading: originsLoading, createOrigin } = useOrigins();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const phoneLocked = lockPhone || contained;
  const isEdit = Boolean(contact?.id);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitError(null);
    setFormData(loadForm(contact));
  }, [isOpen, contact]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } },
  ) => {
    let { name, value } = e.target;
    if (name === 'whatsapp') {
      if (phoneLocked) return;
      value = formatPhoneWithDDI(value);
    }
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (!contact) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const name = formData.name.trim();
    if (!name) {
      setSubmitError('Informe o nome do contato.');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ ...formData, name });
      if (!contact) {
        setFormData({ ...EMPTY_FORM });
        sessionStorage.removeItem(STORAGE_KEY);
      }
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Erro ao salvar contato.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const saveLabel = isSaving ? 'Salvando...' : contained ? 'Salvar' : isEdit ? 'Salvar Alterações' : 'Salvar Contato';

  return (
    <>
      {!contained && (
        <div className="fixed inset-0 z-40 overlay-premium transition-opacity duration-300" onClick={onClose} aria-hidden="true" />
      )}

      <aside
        className={
          contained
            ? 'absolute inset-0 z-30 flex h-full w-full flex-col bg-surface-0'
            : 'fixed top-0 right-0 z-modal flex h-full w-full flex-col panel-sidebar sm:max-w-[31.25rem]'
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-sheet-title"
      >
        <SidebarHeader
          title={contained ? 'Editar contato' : isEdit ? 'Editar Contato' : 'Novo Contato'}
          subtitle={contained ? undefined : isEdit ? 'Atualize as informações do contato.' : 'Preencha os dados para criar um novo contato.'}
          onClose={onClose}
          onBack={contained ? onClose : undefined}
          hideClose={contained}
          compact={contained}
          titleId="contact-sheet-title"
        />

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-0">
          <div className="scrollbar-premium min-h-0 flex-1 space-y-s5 overflow-y-auto px-s4 py-s5 md:px-s6 md:py-s6">
            {submitError && (
              <div className="rounded-lg border border-danger-500/30 bg-danger-bg px-s4 py-s3 text-sm text-danger-text" role="alert">
                {submitError}
              </div>
            )}

            {TEXT_FIELDS.map((field) => (
              <Input
                key={field.name}
                label={field.label}
                type={field.type}
                name={field.name}
                autoComplete={field.autoComplete}
                value={formData[field.name]}
                onChange={handleChange}
                placeholder={field.placeholder}
                required={'required' in field ? field.required : undefined}
              />
            ))}

            <Input
              label="Telefone / WhatsApp"
              type="tel"
              name="whatsapp"
              autoComplete="tel"
              value={formData.whatsapp}
              onChange={handleChange}
              placeholder="+55 (11) 91234-5678"
              disabled={phoneLocked}
              readOnly={phoneLocked}
              helperText={phoneLocked ? 'O telefone não pode ser alterado.' : undefined}
            />

            {!contained && (
              <div>
                <label className="mb-s2 block text-sm font-medium text-default">Origem</label>
                <ComboboxSelector
                  value={formData.origin_id}
                  onChange={(val) => handleChange({ target: { name: 'origin_id', value: val } })}
                  options={origins}
                  loading={originsLoading}
                  placeholder="Selecione ou crie uma origem..."
                  createLabel="Criar origem"
                  valueKey="id"
                  onCreate={async (name) => {
                    const newOrigin = await createOrigin({ name: name.trim() });
                    return newOrigin?.id || name.trim();
                  }}
                />
              </div>
            )}
          </div>

          <footer
            className={
              contained
                ? 'bg-surface-0 px-s4 py-4 md:px-s6'
                : 'border-t border-border bg-surface-subtle px-4 py-4 md:px-6'
            }
          >
            <Button type="submit" variant="default" size="lg" className="min-h-11 w-full gap-s2 touch-manipulation" disabled={isSaving}>
              <CheckIcon className="size-5" aria-hidden />
              {saveLabel}
            </Button>
          </footer>
        </form>
      </aside>
    </>
  );
}
